import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicHeader } from '@/components/marketplace/PublicHeader';
import { useCart } from '@/context/CartContext';
import { formatMoney, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Trash2, Plus, Minus, ShoppingCart, CreditCard, Wallet, ArrowRight, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import { SafeImage } from '@/components/marketplace/SafeImage';
import { toast } from 'sonner';

function CheckoutModal({ open, onClose, items, totalPrice, currency, onClear }) {
    const [step, setStep] = useState('summary'); // 'summary' | 'processing' | 'links'
    const [payLinks, setPayLinks] = useState([]);
    const [wallet, setWallet] = useState(null);
    const [walletLoading, setWalletLoading] = useState(false);

    const loadWallet = async () => {
        try {
            setWalletLoading(true);
            const { data } = await api.get('/wallet/me');
            setWallet(data.wallet);
        } catch {
            setWallet(null);
        } finally {
            setWalletLoading(false);
        }
    };

    const handleOpen = () => {
        setStep('summary');
        setPayLinks([]);
        loadWallet();
    };

    const handlePayWithEcartPay = async () => {
        setStep('processing');
        const links = [];
        const origin = window.location.origin;
        for (const item of items) {
            try {
                const { data } = await api.post('/checkout/ecartpay', {
                    ad_id: item.id,
                    origin_url: `${origin}/ad/${item.id}`,
                });
                links.push({ item, url: data.url, order_id: data.order_id });
            } catch (e) {
                toast.error(`Error al procesar "${item.title}": ${e?.response?.data?.detail || 'Error desconocido'}`);
                links.push({ item, url: null, error: e?.response?.data?.detail || 'No disponible' });
            }
        }
        setPayLinks(links);
        setStep('links');
        if (links.every(l => l.url)) onClear();
    };

    const hasEnoughWallet = wallet && (wallet.balance_mxn >= totalPrice);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); else handleOpen(); }}>
            <DialogContent className="max-w-md rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5" />
                        Proceder al pago
                    </DialogTitle>
                    <DialogDescription>
                        {items.length} {items.length === 1 ? 'artículo' : 'artículos'} · {formatMoney(totalPrice, currency)}
                    </DialogDescription>
                </DialogHeader>

                {step === 'summary' && (
                    <div className="space-y-4">
                        {/* Items summary */}
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {items.map(item => (
                                <div key={item.id} className="flex items-center gap-3 p-2 rounded-xl border bg-card">
                                    <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                                        {item.media?.find(m => m.kind === 'image')?.url
                                            ? <SafeImage src={item.media.find(m => m.kind === 'image').url} alt={item.title} className="w-full h-full object-cover" />
                                            : <div className="w-full h-full bg-muted" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium line-clamp-1">{item.title}</p>
                                        <p className="text-xs text-muted-foreground">@{item.seller_nickname} · x{item.qty}</p>
                                    </div>
                                    <span className="text-sm font-semibold">{formatMoney(item.price * item.qty, item.currency)}</span>
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-between items-center text-lg font-bold border-t pt-3">
                            <span>Total</span>
                            <span>{formatMoney(totalPrice, currency)}</span>
                        </div>

                        {/* OkWallet option */}
                        {walletLoading ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 rounded-xl border">
                                <Loader2 className="h-4 w-4 animate-spin" />Verificando saldo OkWallet…
                            </div>
                        ) : wallet ? (
                            <div className={`p-3 rounded-xl border ${hasEnoughWallet ? 'border-emerald-300 bg-emerald-50' : 'border-muted bg-muted/30'}`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <Wallet className={`h-4 w-4 ${hasEnoughWallet ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                                    <span className="text-sm font-medium">OkWallet</span>
                                    <Badge variant="outline" className="text-xs">${(wallet.balance_mxn || 0).toFixed(2)} disponible</Badge>
                                </div>
                                {!hasEnoughWallet && (
                                    <p className="text-xs text-muted-foreground">Saldo insuficiente para el total del carrito.</p>
                                )}
                            </div>
                        ) : null}

                        {/* Payment buttons */}
                        <div className="space-y-2">
                            <Button
                                className="w-full bg-red-600 hover:bg-red-700 gap-2"
                                onClick={handlePayWithEcartPay}
                            >
                                <CreditCard className="h-4 w-4" />
                                Pagar con tarjeta / EcartPay
                                <ArrowRight className="h-4 w-4 ml-auto" />
                            </Button>
                            <Button variant="outline" className="w-full" onClick={onClose}>
                                Cancelar
                            </Button>
                        </div>

                        <p className="text-xs text-muted-foreground text-center">
                            Cada artículo se procesa por separado. Se abrirá un enlace de pago por vendedor.
                        </p>
                    </div>
                )}

                {step === 'processing' && (
                    <div className="flex flex-col items-center justify-center py-10 gap-4">
                        <Loader2 className="h-10 w-10 animate-spin text-red-600" />
                        <p className="text-sm text-muted-foreground">Generando enlaces de pago…</p>
                    </div>
                )}

                {step === 'links' && (
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Haz clic en cada enlace para completar el pago de cada artículo:
                        </p>
                        <div className="space-y-2">
                            {payLinks.map(({ item, url, error }) => (
                                <div key={item.id} className={`p-3 rounded-xl border flex items-center justify-between gap-3 ${error ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium line-clamp-1">{item.title}</p>
                                        <p className="text-xs text-muted-foreground">{formatMoney(item.price, item.currency)}</p>
                                        {error && <p className="text-xs text-red-600">{error}</p>}
                                    </div>
                                    {url ? (
                                        <a href={url} target="_blank" rel="noreferrer">
                                            <Button size="sm" className="bg-red-600 hover:bg-red-700 gap-1 shrink-0">
                                                Pagar <ExternalLink className="h-3 w-3" />
                                            </Button>
                                        </a>
                                    ) : (
                                        <Badge className="bg-red-100 text-red-800 shrink-0">Error</Badge>
                                    )}
                                </div>
                            ))}
                        </div>
                        {payLinks.every(l => l.url) && (
                            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl p-3">
                                <CheckCircle2 className="h-4 w-4 shrink-0" />
                                Tu carrito fue procesado. Completa cada pago en los enlaces de arriba.
                            </div>
                        )}
                        <Button variant="outline" className="w-full" onClick={onClose}>Cerrar</Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function CartPage() {
    const { items, removeItem, updateQty, totalItems, totalPrice, clearCart } = useCart();
    const navigate = useNavigate();
    const [checkoutOpen, setCheckoutOpen] = useState(false);

    const currency = items[0]?.currency || 'MXN';

    return (
        <div className="min-h-screen bg-background">
            <PublicHeader />
            <main className="max-w-3xl mx-auto px-4 py-8">
                <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
                    <ShoppingCart className="h-6 w-6" />
                    Mi carrito
                    {totalItems > 0 && <span className="text-base font-normal text-muted-foreground">({totalItems} {totalItems === 1 ? 'artículo' : 'artículos'})</span>}
                </h1>

                {items.length === 0 ? (
                    <div className="text-center py-20">
                        <ShoppingCart className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground text-lg mb-6">Tu carrito está vacío</p>
                        <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
                            Explorar productos
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {items.map(item => {
                            const image = item.media?.find(m => m.kind === 'image')?.url;
                            return (
                                <div key={item.id} className="flex gap-4 p-4 rounded-2xl border bg-card shadow-sm">
                                    <Link to={`/ad/${item.id}`} className="flex-shrink-0">
                                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted">
                                            {image
                                                ? <SafeImage src={image} alt={item.title} className="w-full h-full object-cover" />
                                                : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Sin foto</div>
                                            }
                                        </div>
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                        <Link to={`/ad/${item.id}`} className="font-medium text-sm line-clamp-2 hover:text-red-600">{item.title}</Link>
                                        <p className="text-xs text-muted-foreground mt-0.5">@{item.seller_nickname}</p>
                                        <p className="text-base font-semibold mt-1">{formatMoney(item.price, item.currency)}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <button
                                                className="h-7 w-7 rounded-full border flex items-center justify-center hover:bg-secondary"
                                                onClick={() => updateQty(item.id, item.qty - 1)}
                                            >
                                                <Minus className="h-3 w-3" />
                                            </button>
                                            <span className="text-sm font-medium w-6 text-center">{item.qty}</span>
                                            <button
                                                className="h-7 w-7 rounded-full border flex items-center justify-center hover:bg-secondary"
                                                onClick={() => updateQty(item.id, item.qty + 1)}
                                            >
                                                <Plus className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        className="flex-shrink-0 self-start p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
                                        onClick={() => removeItem(item.id)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            );
                        })}

                        <div className="mt-2 p-4 rounded-2xl border bg-card">
                            <div className="flex justify-between text-sm text-muted-foreground mb-1">
                                <span>Subtotal ({totalItems} artículos)</span>
                            </div>
                            <div className="flex justify-between text-xl font-bold">
                                <span>Total</span>
                                <span>{formatMoney(totalPrice, currency)}</span>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-2">
                            <Button variant="outline" className="flex-1" onClick={() => navigate('/')}>
                                Seguir comprando
                            </Button>
                            <Button className="flex-1 bg-red-600 hover:bg-red-700 gap-2" onClick={() => setCheckoutOpen(true)}>
                                <CreditCard className="h-4 w-4" />
                                Proceder al pago
                            </Button>
                        </div>

                        <button
                            className="text-xs text-muted-foreground hover:text-red-600 text-center mt-1"
                            onClick={clearCart}
                        >
                            Vaciar carrito
                        </button>
                    </div>
                )}
            </main>

            <CheckoutModal
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                items={items}
                totalPrice={totalPrice}
                currency={currency}
                onClear={clearCart}
            />
        </div>
    );
}
